import { API_BASE_URL, CORS_PROXY } from './constants';
import { getWithExpiry, setWithExpiry } from './storage';

const isDev = process.env.NODE_ENV === 'development';

class ApiService {
  constructor() {
    this.timeout = 10000;
    this.cacheSettings = {
      enabled: true,
      ttl: 7
    };
    this.loadCacheSettings();
  }

  loadCacheSettings() {
    try {
      const saved = localStorage.getItem('cache_settings');
      if (saved) this.cacheSettings = JSON.parse(saved);
    } catch {}
  }

  // Convert absolute API URL to local proxy URL for dev mode
  toLocalUrl(url) {
    if (isDev && url.startsWith(API_BASE_URL)) {
      return url.replace(API_BASE_URL, '/api');
    }
    return url;
  }

  async makeRequest(url, options = {}, useCache = true, cacheKey = null, cacheTTL = null) {
    if (!this.cacheSettings.enabled) useCache = false;

    const isOnline = navigator.onLine;
    const finalCacheKey = cacheKey || `api_${btoa(url)}`;
    const finalTTL = cacheTTL || (this.cacheSettings.ttl * 24 * 60 * 60 * 1000);

    let cachedItem = null;
    if (useCache) {
      try {
        const itemStr = localStorage.getItem(finalCacheKey);
        if (itemStr) {
          const item = JSON.parse(itemStr);
          if (Date.now() - item.timestamp <= finalTTL) {
            return { data: item.data, source: 'cache', cacheInfo: { cacheDate: new Date(item.timestamp).toISOString() } };
          }
          cachedItem = item;
        }
      } catch {}
    }

    if (!isOnline) {
      if (cachedItem) return { data: cachedItem.data, source: 'stale_cache', cacheInfo: { cacheDate: new Date(cachedItem.timestamp).toISOString() } };
      throw new Error('NO_INTERNET');
    }

    // In dev mode, use local proxy first; in production, try direct URL
    const fetchUrl = this.toLocalUrl(url);
    try {
      const response = await this.fetchWithTimeout(fetchUrl, options);
      const data = await this.safeJsonParse(response);
      if (useCache) {
        try { localStorage.setItem(finalCacheKey, JSON.stringify({ data, timestamp: Date.now(), url })); } catch {}
      }
      return { data, source: isDev ? 'proxy' : 'api', cacheInfo: null };
    } catch (directError) {
      // Использовать прокси только при ошибке CORS
      const msg = (directError && directError.message) ? directError.message : String(directError);
      const isCORS =
        msg.includes('Access-Control-Allow-Origin') ||
        msg.includes('CORS') ||
        msg.includes('Failed to fetch') ||
        msg.includes('fetch') && msg.includes('due to access control checks');
      if (!isCORS) {
        if (cachedItem) return { data: cachedItem.data, source: 'stale_cache', cacheInfo: { cacheDate: new Date(cachedItem.timestamp).toISOString() } };
        throw directError;
      }
      try {
        const proxyUrl = `${CORS_PROXY}${encodeURIComponent(url)}`;
        const response = await this.fetchWithTimeout(proxyUrl, options);
        const wrapped = await this.safeJsonParse(response);
        // /get endpoint wraps response in { contents: "...", status: {...} }
        let data = wrapped;
        if (wrapped && typeof wrapped.contents === 'string') {
          try { data = JSON.parse(wrapped.contents); } catch { data = wrapped.contents; }
        }
        if (useCache) {
          try { localStorage.setItem(finalCacheKey, JSON.stringify({ data, timestamp: Date.now(), url })); } catch {}
        }
        return { data, source: 'proxy', cacheInfo: null };
      } catch {
        if (cachedItem) return { data: cachedItem.data, source: 'stale_cache', cacheInfo: { cacheDate: new Date(cachedItem.timestamp).toISOString() } };
        throw new Error('API_UNAVAILABLE');
      }
    }
  }

  async getNews(from = 0, amount = 10) {
    const url = `${API_BASE_URL}/news?amount=${amount}&from=${from}`;
    return this.makeRequest(url, {}, true, `news_${from}_${amount}`, 30 * 60 * 1000);
  }

  async getGroups(course) {
    const url = `${API_BASE_URL}/getgroups/${course}`;
    return this.makeRequest(url, {}, true, `groups_${course}`, 24 * 60 * 60 * 1000);
  }

  async getSchedule(group, date, week = null) {
    let url, cacheKey;
    if (week) {
      url = `${API_BASE_URL}/getpairsweek?type=group&data=${group}&week=${week}`;
      cacheKey = `schedule_${group}_week_${week}`;
    } else {
      const formattedDate = typeof date === 'string' ? date : this.formatDate(date);
      url = `${API_BASE_URL}/getpairs/date:${group}:${formattedDate}`;
      cacheKey = `schedule_${group}_date_${formattedDate}`;
    }
    return this.makeRequest(url, {}, true, cacheKey, 60 * 60 * 1000);
  }

  async getPairsTime() {
    const url = `${API_BASE_URL}/getpairstime`;
    return this.makeRequest(url, {}, true, 'pairs_time', 7 * 24 * 60 * 60 * 1000);
  }

  async getCourses() {
    const url = `${API_BASE_URL}/getcourses`;
    return this.makeRequest(url, {}, true, 'available_courses', 24 * 60 * 60 * 1000);
  }

  async getWeekNumbers() {
    const url = `${API_BASE_URL}/weeknumbers`;
    return this.makeRequest(url, {}, true, 'week_numbers', 6 * 60 * 60 * 1000);
  }

  async getTeacherSchedule(teacher, week) {
    const url = `${API_BASE_URL}/getpairsweek?type=teacher&data=${encodeURIComponent(teacher)}&week=${week}`;
    return this.makeRequest(url, {}, true, `teacher_schedule_${teacher}_${week}`, 60 * 60 * 1000);
  }

  async getAuditorySchedule(auditory, week) {
    const url = `${API_BASE_URL}/getpairsweek?type=auditory&data=${encodeURIComponent(auditory)}&week=${week}`;
    return this.makeRequest(url, {}, true, `auditory_schedule_${auditory}_${week}`, 60 * 60 * 1000);
  }

  async search(query) {
    const url = `${API_BASE_URL}/search/${encodeURIComponent(query)}`;
    return this.makeRequest(url, {}, true, `search_${query}`, 10 * 60 * 1000);
  }

  formatDate(date) {
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '.');
  }

  async fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  async safeJsonParse(response) {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      if (text.includes('<')) throw new Error('HTML_RESPONSE');
      throw new Error('JSON_PARSE_ERROR');
    }
  }
}

const apiService = new ApiService();
export default apiService;
